// Simplified download manager: single inflight chunk request, strict CRC, deterministic finalize

import { reactive, ref } from 'vue'
import {
    readResumeState,
    writeResumeState,
    clearResumeState,
    listResumeFiles,
    writeChunkBatch,
    readChunks,
    clearChunks,
    listChunkFiles,
    clearAllDownloadCache,
} from '@/lib/sqlite/features/reinforce/download-cache-feature'
import {
    listReinforceDownloadMetaKeys,
    listReinforceDownloadMetaFileNames,
    clearReinforceDownloadMetaFor,
    makeReinforceDownloadResumeKey,
    makeReinforceDownloadChunksKey,
} from '@/lib/sqlite/features/reinforce/memory-cache-feature'

export function createDownloadManager(ctx) {
    const {
        BleClient,
        textToDataView,
        constants: { STREAMING_SERVICE, COMMAND_CHAR, FILE_CHAR },
        state: {
            properties,
            downloadingFile,
            downloadTotalBytes,
            downloadReceivedBytes,
            downloadFileName,
            downloadCompletionState,
            downloadErrorReason,
            downloadBaseOffset,
            rowsPerSec,
            sdFiles,
            staleCache,
        },
        memory: { memoryGet, memorySet },
        helpers: {
            formatDate,
            formatDuration,
            normalizeLabel,
            extractLabelFromFilename,
            formatCsv,
            readCachedNumber,
            wrapCachedNumber,
        },
        forceModel,
        getDevice,
        writeCommand,
    } = ctx

    const downloadStats = reactive({
        windowSec: 30,
        pktsPerMin: 0,
        linesPerMin: 0,
        avgPktBytes: 0,
        linesObserved: 0,
        avgBytesPerLine: 0,
        durationObservedSec: 0,
        durationEstimatedSec: null,
        bytesPerSec: 0,
        missingChunksBeforeRecovery: 0,
        crcErrors: 0,
        activeCrcFailures: 0,
    })
    const downloadMissing = reactive({
        bytes: 0,
        chunks: 0,
    })

    const collectingSessions = ref(false)
    const sessionsBuffer = ref([])
    const recoveringChunks = ref(false)
    const resumeReload = reactive({ phase: null, processed: 0, total: 0 })
    const sessionsExpectedCount = ref(0)
    const sessionsReceivedCount = ref(0)
    const sessionsRequestId = ref(0)
    const sessionsCompletedId = ref(0)
    const sessionsRetryCount = ref(0)

    const resumePosToken = ref(null)
    const chunkCount = ref(0)
    const persistedContiguous = ref(0)
    const crcErrorCount = ref(0)
    const downloadReadyUrl = ref('')
    const downloadReadyName = ref('')
    const downloadReadyAt = ref(0)

    const CHUNK_SIZE = 238
    const MAX_CHUNK_REQUEST = CHUNK_SIZE * 256
    const COMMAND_MAX_BYTES = 63
    const MAX_CHUNK_RETRIES = 5
    const CHUNK_REQUEST_TIMEOUT_MS = 15000
    const CHUNK_RETRY_DELAY_MS = 200
    const RESUME_PERSIST_INTERVAL_MS = 1500
    const RESUME_PERSIST_MIN_BYTES = 4096
    const CHUNK_FLUSH_INTERVAL_MS = 400
    const CHUNK_FLUSH_MAX = 40
    const MISSING_RECHECK_DELAY_MS = 150
    const FINALIZE_QUIET_WINDOW_MS = 400
    const SESSIONS_RETRY_DELAY_MS = 300
    const MAX_SESSIONS_RETRIES = 1
    const DELETE_TIMEOUT_MS = 5000
    const DELETE_ERROR_CODES = new Set([
        'delete-failed',
        'invalid-name',
        'sd-not-exfat',
        'storage-disabled',
        'busy-recording',
        'busy-xfer',
    ])

    let sessionsTimeoutId = null
    let finalizeTimerId = null
    let sessionsSeen = new Set()
    let resumeMode = false
    let resumePersistAt = 0
    let resumePersistOffset = 0
    let resumePersistName = ''
    let chunkFlushTimer = null
    let chunkFlushInFlight = false
    let deleteWaiter = null
    const pendingChunkWrites = []

    const crc16Table = new Uint16Array(256)
    for (let i = 0; i < 256; i++) {
        let crc = i << 8
        for (let j = 0; j < 8; j++) {
            crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1
        }
        crc16Table[i] = crc & 0xffff
    }

    function crc16(data) {
        let crc = 0xffff
        for (let i = 0; i < data.length; i++) {
            crc = ((crc << 8) ^ crc16Table[((crc >> 8) ^ data[i]) & 0xff]) & 0xffff
        }
        return crc
    }

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

    const resetDownloadStatsState = () => {
        Object.assign(downloadStats, {
            pktsPerMin: 0,
            linesPerMin: 0,
            avgPktBytes: 0,
            linesObserved: 0,
            avgBytesPerLine: 0,
            durationObservedSec: 0,
            durationEstimatedSec: null,
            bytesPerSec: 0,
            missingChunksBeforeRecovery: 0,
            crcErrors: 0,
            activeCrcFailures: 0,
        })
        crcErrorCount.value = 0
    }

    const clearEtaState = () => {
        downloadStats.durationEstimatedSec = null
    }

    const scheduleChunkFlush = () => {
        if (chunkFlushTimer) return
        chunkFlushTimer = setTimeout(() => {
            chunkFlushTimer = null
            flushChunkWrites().catch((error) => {
                console.warn('[Download] Chunk flush failed', error)
            })
        }, CHUNK_FLUSH_INTERVAL_MS)
    }

    const flushChunkWrites = async () => {
        if (chunkFlushInFlight || pendingChunkWrites.length === 0) return
        chunkFlushInFlight = true
        const batch = pendingChunkWrites.splice(0, pendingChunkWrites.length)
        const grouped = new Map()
        for (const item of batch) {
            if (!item?.filename) continue
            if (!grouped.has(item.filename)) grouped.set(item.filename, [])
            grouped.get(item.filename).push({ offset: item.offset, bytes: item.bytes })
        }
        try {
            for (const [filename, chunks] of grouped.entries()) {
                await writeChunkBatch(filename, chunks)
            }
        } finally {
            chunkFlushInFlight = false
            if (pendingChunkWrites.length > 0) scheduleChunkFlush()
        }
    }

    const queueChunkPersist = (offset, bytes) => {
        if (!download.filename || !bytes || bytes.length === 0) return
        pendingChunkWrites.push({ filename: download.filename, offset, bytes })
        if (pendingChunkWrites.length >= CHUNK_FLUSH_MAX) {
            flushChunkWrites().catch((error) => console.warn('[Download] Chunk flush failed', error))
            return
        }
        scheduleChunkFlush()
    }

    const persistResumeProgress = async ({ offset, posToken } = {}) => {
        if (!download.filename) return
        const now = Date.now()
        const nextOffset = Number.isFinite(offset) ? Math.floor(offset) : Math.floor(download.receivedBytes || 0)
        const token = posToken || resumePosToken.value || ''
        if (!token && (!Number.isFinite(nextOffset) || nextOffset <= 0)) return

        const sameFile = resumePersistName === download.filename
        const offsetDelta = Math.abs(nextOffset - resumePersistOffset)
        if (sameFile && offsetDelta < RESUME_PERSIST_MIN_BYTES && now - resumePersistAt < RESUME_PERSIST_INTERVAL_MS) {
            return
        }

        resumePersistAt = now
        resumePersistOffset = nextOffset
        resumePersistName = download.filename
        await writeResumeState(download.filename, {
            offset: nextOffset,
            posToken: token,
            totalSize: download.totalSize || 0,
        })
        try {
            await memorySet(`resume:${download.filename}`, wrapCachedNumber(nextOffset))
        } catch (e) {
            console.warn('[Download] Memory resume persist failed', e)
        }
    }

    const readResumeMemoryOffset = async (filename) => {
        if (!filename) return null
        const raw = await memoryGet(`resume:${filename}`)
        const offset = readCachedNumber(raw)
        if (Number.isFinite(offset) && offset > 0) {
            const hasUpdatedAt = raw && typeof raw === 'object' && Number.isFinite(Number(raw.updatedAt))
            if (!hasUpdatedAt) {
                try {
                    await memorySet(`resume:${filename}`, wrapCachedNumber(offset))
                } catch {}
            }
        }
        return offset
    }

    const updateMissingStats = () => {
        const total = download.totalSize
        if (total > 0) {
            const missingBytes = Math.max(0, total - download.storedBytes)
            downloadMissing.bytes = missingBytes
            downloadMissing.chunks = missingBytes > 0 ? Math.ceil(missingBytes / CHUNK_SIZE) : 0
        } else {
            downloadMissing.bytes = 0
            downloadMissing.chunks = 0
        }
    }

    const clearDownloadReady = () => {
        if (downloadReadyUrl.value) {
            try {
                URL.revokeObjectURL(downloadReadyUrl.value)
            } catch {}
        }
        downloadReadyUrl.value = ''
        downloadReadyName.value = ''
        downloadReadyAt.value = 0
    }

    const setDownloadReady = (url, name) => {
        clearDownloadReady()
        downloadReadyUrl.value = url
        downloadReadyName.value = name || ''
        downloadReadyAt.value = Date.now()
    }

    const setDownloadUiState = (active) => {
        const isActive = !!active
        downloadingFile.value = isActive
        properties.downloadActive = isActive
        properties.downloadText = isActive ? 'ON' : 'OFF'
    }

    const finishDeleteWait = (ok) => {
        const waiter = deleteWaiter
        if (!waiter) return
        deleteWaiter = null
        if (waiter.timeoutId) {
            clearTimeout(waiter.timeoutId)
        }
        try {
            waiter.resolve(!!ok)
        } catch {}
    }

    const startDeleteWait = (filename, timeoutMs = DELETE_TIMEOUT_MS) => {
        if (!filename) return Promise.resolve(false)
        if (deleteWaiter && deleteWaiter.timeoutId) {
            clearTimeout(deleteWaiter.timeoutId)
        }
        return new Promise((resolve) => {
            deleteWaiter = {
                filename,
                resolve,
                timeoutId: setTimeout(() => finishDeleteWait(false), timeoutMs),
            }
        })
    }

    const handleDeleteStatus = (text) => {
        if (!deleteWaiter || !text) return false
        if (text.startsWith('deleted=')) {
            const name = text.slice(8).split(',')[0].trim()
            if (!name || name === deleteWaiter.filename) {
                finishDeleteWait(true)
                return true
            }
            return false
        }
        if (text.startsWith('error=')) {
            const reason = text.slice(6).trim()
            if (DELETE_ERROR_CODES.has(reason)) {
                finishDeleteWait(false)
                return true
            }
        }
        return false
    }

    const resetSessionsState = () => {
        collectingSessions.value = true
        sessionsBuffer.value = []
        sessionsExpectedCount.value = 0
        sessionsReceivedCount.value = 0
        sessionsSeen = new Set()
    }

    const scheduleSessionsTimeout = () => {
        clearSessionsTimeout()
        sessionsTimeoutId = setTimeout(() => {
            handleSessionsTimeout().catch((error) => {
                console.warn('[Download] Sessions timeout handling failed', error)
            })
        }, 10000)
    }

    const startSessionsRequest = async ({ resetRetry = true } = {}) => {
        if (resetRetry) sessionsRetryCount.value = 0
        resetSessionsState()
        scheduleSessionsTimeout()
        await writeCommand('sessions?')
    }

    const maybeRetrySessions = async (reason) => {
        if (sessionsRetryCount.value >= MAX_SESSIONS_RETRIES) return false
        sessionsRetryCount.value += 1
        console.warn('[Download] Sessions incomplete, retrying', {
            reason,
            expected: sessionsExpectedCount.value,
            received: sessionsReceivedCount.value,
        })
        await delay(SESSIONS_RETRY_DELAY_MS)
        resetSessionsState()
        scheduleSessionsTimeout()
        await writeCommand('sessions?')
        return true
    }

    const handleSessionsTimeout = async () => {
        if (!collectingSessions.value) return
        if (sessionsExpectedCount.value > 0 && sessionsReceivedCount.value < sessionsExpectedCount.value) {
            const retried = await maybeRetrySessions('timeout')
            if (retried) return
        }
        await markSessionsCompleted()
    }

    const triggerBrowserDownload = (url, filename) => {
        if (!url) return
        try {
            const anchor = document.createElement('a')
            anchor.href = url
            anchor.download = filename || 'download.csv'
            anchor.style.display = 'none'
            document.body.appendChild(anchor)
            anchor.click()
            document.body.removeChild(anchor)
        } catch (error) {
            console.warn('[Download] Trigger download failed', error)
        }
    }

    const download = {
        active: false,
        filename: '',
        totalSize: 0,
        receivedBytes: 0,
        storedBytes: 0,
        startTime: 0,
        sessionStartBytes: 0,
        baseOffset: 0,
        chunks: new Map(),
        formatMode: false,
        formatModel: null,
        contentReceiver: null,
        paused: false,
        finalized: false,
        requestInFlight: false,
        requestOffset: null,
        requestLen: 0,
        requestTimeoutId: null,
        retries: new Map(),
        endReceived: false,
        lastFileChunkAt: 0,
        logged100: false,
        fileChunkRx: 0,
        fileChunkStored: 0,
        fileChunkDropped: 0,
    }

    const clearFinalizeTimer = () => {
        if (finalizeTimerId) {
            clearTimeout(finalizeTimerId)
            finalizeTimerId = null
        }
    }

    const scheduleFinalize = (delayMs = 0) => {
        clearFinalizeTimer()
        finalizeTimerId = setTimeout(() => {
            finalizeTimerId = null
            attemptFinalize()
        }, Math.max(0, delayMs))
    }

    const attemptFinalize = () => {
        if (!download.active || download.finalized) return
        if (download.requestInFlight) return
        const now = Date.now()
        if (now - download.lastFileChunkAt < FINALIZE_QUIET_WINDOW_MS) {
            const wait = FINALIZE_QUIET_WINDOW_MS - (now - download.lastFileChunkAt)
            scheduleFinalize(wait)
            return
        }
        finalizeDownload().catch((error) => {
            console.error('[Download] Finalize failed', error)
            resetDownload('error', 'chunk-finalize-failed')
        })
    }

    const clearChunkTimeout = () => {
        if (download.requestTimeoutId) {
            clearTimeout(download.requestTimeoutId)
            download.requestTimeoutId = null
        }
    }

    const getSortedChunks = () => {
        if (download.chunks.size === 0) return []
        return Array.from(download.chunks.entries()).sort((a, b) => a[0] - b[0])
    }

    const findFirstMissingRange = (sortedChunks, expectedSize) => {
        let cursor = 0
        for (const [offset, chunk] of sortedChunks) {
            if (offset > cursor) return { start: cursor, length: offset - cursor }
            const end = offset + chunk.length
            if (end > cursor) cursor = end
        }
        if (expectedSize && cursor < expectedSize) {
            return { start: cursor, length: expectedSize - cursor }
        }
        return null
    }

    const getContiguousEnd = (sortedChunks) => {
        let cursor = 0
        for (const [offset, chunk] of sortedChunks) {
            if (offset > cursor) break
            const end = offset + chunk.length
            if (end > cursor) cursor = end
        }
        return cursor
    }

    const updateStatsWithChunk = (chunkBytes) => {
        const now = Date.now()
        if (!download.startTime) download.startTime = now
        const elapsed = (now - download.startTime) / 1000
        if (elapsed > 0) {
            const sessionBytes = Math.max(0, download.receivedBytes - (download.sessionStartBytes || 0))
            downloadStats.bytesPerSec = Math.round(sessionBytes / elapsed)
        }

        let newLines = 0
        for (let i = 0; i < chunkBytes.length; i++) {
            if (chunkBytes[i] === 10) newLines += 1
        }
        if (newLines > 0) {
            downloadStats.linesObserved += newLines
            if (downloadStats.linesObserved > 0) {
                downloadStats.avgBytesPerLine = Math.round(download.receivedBytes / downloadStats.linesObserved)
            }
        }

        if (download.totalSize > 0) {
            const remaining = Math.max(0, download.totalSize - download.receivedBytes)
            if (downloadStats.bytesPerSec > 0) {
                downloadStats.durationEstimatedSec = Math.round(remaining / downloadStats.bytesPerSec)
            }
        }
    }

    const requestFirmwareChunk = (start, length) => {
        if (!download.filename) return false
        if (download.requestInFlight) return false

        const normalizedStart = Math.max(0, Number.isFinite(start) ? Math.round(start) : 0)
        const desiredLength = Number.isFinite(length) && length > 0 ? Math.round(length) : CHUNK_SIZE
        const requestedLength = Math.max(1, Math.min(desiredLength, MAX_CHUNK_REQUEST))

        const buildCommand = (len, includeLen) =>
            includeLen
                ? `chunk=${download.filename},from=${normalizedStart},len=${len}`
                : `chunk=${download.filename},from=${normalizedStart}`

        let includeLen = true
        let command = buildCommand(requestedLength, includeLen)
        if (command.length >= COMMAND_MAX_BYTES) {
            includeLen = false
            command = buildCommand(CHUNK_SIZE, includeLen)
        }
        if (command.length >= COMMAND_MAX_BYTES) {
            resetDownload('error', 'chunk-command-too-long')
            return false
        }

        download.requestInFlight = true
        download.requestOffset = normalizedStart
        download.requestLen = requestedLength

        clearChunkTimeout()
        writeCommand(command).catch((error) => {
            console.error('[Download] Chunk command failed', error)
            download.requestInFlight = false
            download.requestOffset = null
            download.requestLen = 0
            clearChunkTimeout()
            resetDownload('error', 'chunk-send-failed')
        })

        download.requestTimeoutId = setTimeout(() => {
            download.requestTimeoutId = null
            const retryOffset = download.requestOffset
            const retryLen = download.requestLen || CHUNK_SIZE
            download.requestInFlight = false
            download.requestOffset = null
            download.requestLen = 0

            if (Number.isFinite(retryOffset)) {
                const retryKey = String(retryOffset)
                const attempts = (download.retries.get(retryKey) || 0) + 1
                download.retries.set(retryKey, attempts)
                if (attempts <= MAX_CHUNK_RETRIES) {
                    console.warn('[Download] Chunk timeout, retrying', {
                        offset: retryOffset,
                        len: retryLen,
                        attempts,
                    })
                    setTimeout(() => {
                        requestFirmwareChunk(retryOffset, retryLen)
                    }, CHUNK_RETRY_DELAY_MS)
                    return
                }
            }

            resetDownload('error', 'chunk-timeout')
        }, CHUNK_REQUEST_TIMEOUT_MS)

        return true
    }

    const finalizeDownload = async () => {
        if (!download.active || download.finalized) return
        if (download.requestInFlight) return
        console.log('[Download] Finalize start', {
            totalSize: download.totalSize,
            receivedBytes: download.receivedBytes,
            chunks: download.chunks.size,
            endReceived: download.endReceived,
            lastFileChunkAt: download.lastFileChunkAt,
        })

        const expectedSize = download.totalSize > 0 ? download.totalSize : download.receivedBytes
        const sortedChunks = getSortedChunks()
        const missing = findFirstMissingRange(sortedChunks, expectedSize)

        if (missing) {
            console.log('[Download] Missing range detected', missing)
            recoveringChunks.value = true
            downloadStats.missingChunksBeforeRecovery = 1
            await delay(MISSING_RECHECK_DELAY_MS)
            const stillMissing = findFirstMissingRange(getSortedChunks(), expectedSize)
            if (!stillMissing) {
                recoveringChunks.value = false
                return finalizeDownload()
            }
            console.log('[Download] Missing range confirmed', stillMissing)
            const retryKey = String(stillMissing.start)
            const currentRetries = download.retries.get(retryKey) || 0
            if (currentRetries >= MAX_CHUNK_RETRIES) {
                resetDownload('error', 'missing-chunks-exceeded-retries')
                return
            }
            download.retries.set(retryKey, currentRetries + 1)
            requestFirmwareChunk(stillMissing.start, Math.min(CHUNK_SIZE, stillMissing.length))
            return
        }

        recoveringChunks.value = false
        downloadStats.missingChunksBeforeRecovery = 0

        const fullLength = expectedSize || download.receivedBytes
        const assembled = new Uint8Array(fullLength)
        for (const [offset, bytes] of sortedChunks) {
            assembled.set(bytes, offset)
        }

        let text = ''
        const decoder = typeof TextDecoder !== 'undefined' ? new TextDecoder() : null
        if (decoder) {
            text = decoder.decode(assembled)
        } else {
            const chunkSize = 8192
            for (let i = 0; i < assembled.length; i += chunkSize) {
                const slice = assembled.subarray(i, i + chunkSize)
                text += String.fromCharCode(...slice)
            }
        }
        const model =
            download.formatModel || (typeof forceModel === 'function' ? forceModel() : forceModel)
        const finalData = download.formatMode ? formatCsv(text, model || undefined) : text

        if (download.contentReceiver) {
            download.contentReceiver(finalData)
        } else {
            const blob = new Blob([finalData], { type: 'text/csv' })
            const url = URL.createObjectURL(blob)
            const filename = download.filename
            setDownloadReady(url, filename)
            triggerBrowserDownload(url, filename)
        }

        download.finalized = true
        clearChunkTimeout()
        const finishedName = download.filename
        resetDownload('success')
        if (finishedName) {
            if (chunkFlushTimer) {
                try {
                    clearTimeout(chunkFlushTimer)
                } catch {}
                chunkFlushTimer = null
            }
            pendingChunkWrites.length = 0
            clearResumeState(finishedName).catch((e) => console.warn('[Download] Clear resume failed', e))
            clearChunks(finishedName).catch((e) => console.warn('[Download] Clear chunks failed', e))
        }
    }

        const handleFileChunkDv = (dataView) => {
        if (!download.active || dataView.byteLength < 6) {
            if (dataView && dataView.byteLength >= 6) {
                download.fileChunkDropped += 1
                console.warn('[Download] FILE dropped (inactive)', {
                    active: download.active,
                    len: dataView.byteLength,
                })
            }
            return
        }

        download.lastFileChunkAt = Date.now()
        download.fileChunkRx += 1
        const offset = dataView.getUint32(0, true)
        const receivedCrc = dataView.getUint16(4, true)
        const chunkLength = dataView.byteLength - 6
        if (chunkLength <= 0) return

        const chunkBytes = new Uint8Array(chunkLength)
        chunkBytes.set(new Uint8Array(dataView.buffer, dataView.byteOffset + 6, chunkLength))

        const requestOffset = download.requestOffset
        if (
            download.requestInFlight &&
            Number.isFinite(requestOffset) &&
            offset >= requestOffset &&
            offset < requestOffset + Math.max(1, download.requestLen || chunkLength)
        ) {
            clearChunkTimeout()
            download.requestInFlight = false
            download.requestOffset = null
            download.requestLen = 0
        }

        const calculatedCrc = crc16(chunkBytes)
        if (receivedCrc !== calculatedCrc) {
            downloadStats.crcErrors += 1
            crcErrorCount.value = downloadStats.crcErrors
            download.fileChunkDropped += 1
            console.warn('[Download] CRC mismatch', {
                offset,
                receivedCrc,
                calculatedCrc,
                len: chunkLength,
                rx: download.fileChunkRx,
                stored: download.fileChunkStored,
                dropped: download.fileChunkDropped,
            })
            const retryKey = String(offset)
            const attempts = (download.retries.get(retryKey) || 0) + 1
            download.retries.set(retryKey, attempts)
            if (attempts > MAX_CHUNK_RETRIES) {
                resetDownload('error', 'chunk-crc-failed')
                return
            }
            requestFirmwareChunk(offset, chunkLength)
            return
        }

        const isNewChunk = !download.chunks.has(offset)
        download.chunks.set(offset, chunkBytes)
        if (isNewChunk) {
            download.storedBytes += chunkBytes.length
            queueChunkPersist(offset, chunkBytes)
        }
        download.fileChunkStored += 1
        chunkCount.value = download.chunks.size
        download.receivedBytes = Math.max(download.receivedBytes, offset + chunkBytes.length)

        downloadReceivedBytes.value = download.receivedBytes
        updateMissingStats()
        updateStatsWithChunk(chunkBytes)
        if (
            download.receivedBytes - resumePersistOffset >= RESUME_PERSIST_MIN_BYTES ||
            Date.now() - resumePersistAt >= RESUME_PERSIST_INTERVAL_MS
        ) {
            persistResumeProgress({ offset: download.receivedBytes, posToken: resumePosToken.value }).catch((e) =>
                console.warn('[Download] Persist resume failed', e)
            )
        }
        if (!download.logged100 && download.totalSize > 0 && download.receivedBytes >= download.totalSize) {
            download.logged100 = true
            console.log('[Download] Reached 100%', {
                totalSize: download.totalSize,
                receivedBytes: download.receivedBytes,
                chunks: download.chunks.size,
                crcErrors: downloadStats.crcErrors,
                lastChunkOffset: offset,
            })
            scheduleFinalize(FINALIZE_QUIET_WINDOW_MS)
        }
        if (download.endReceived) {
            scheduleFinalize(FINALIZE_QUIET_WINDOW_MS)
        }
    }

    const handleFtError = async (text) => {
        const error = text.replace('error=', '')
        resetDownload('error', error)
    }

    const handleFtLine = async (text) => {
        if (!text) return false
        text = text.trim()
        if (!text) return false

        let cachedFields = null
        const ensureFields = () => {
            if (cachedFields) return cachedFields
            cachedFields = {}
            const pieces = String(text).split(',')
            for (const piece of pieces) {
                const [rawKey, rawValue] = piece.split('=')
                if (!rawKey || rawValue === undefined) continue
                const key = rawKey.trim()
                const value = rawValue.trim()
                const numeric = Number(value)
                cachedFields[key] = Number.isFinite(numeric) ? numeric : value
            }
            return cachedFields
        }

        const getNumericField = (...keys) => {
            const fields = ensureFields()
            for (const key of keys) {
                if (key in fields && Number.isFinite(fields[key])) return fields[key]
            }
            return null
        }

        if (text.startsWith('ft=chunk-error')) {
            clearChunkTimeout()
            download.requestInFlight = false
            resetDownload('error', 'chunk-failed')
            return true
        }

        if (text.startsWith('ft=chunk-end')) {
            clearChunkTimeout()
            download.requestInFlight = false
            download.lastFileChunkAt = Date.now()
            scheduleFinalize(FINALIZE_QUIET_WINDOW_MS)
            return true
        }

        if (text.startsWith('ft=start')) {
            const match = text.match(/name=([^,]+),size=(\d+),offset=(\d+)/)
            if (match) {
                const resumeSame = resumeMode && download.filename && download.filename === match[1]
                const prevReceived = download.receivedBytes
                download.active = true
                download.paused = false
                download.finalized = false
                download.filename = match[1]
                download.totalSize = parseInt(match[2]) || 0
                const startOffset = parseInt(match[3]) || 0
                download.receivedBytes = startOffset
                if (resumeSame && prevReceived > startOffset) {
                    download.receivedBytes = prevReceived
                }
                if (!resumeSame) {
                    download.storedBytes = 0
                }
                download.baseOffset = startOffset
                download.startTime = Date.now()
                download.sessionStartBytes = download.receivedBytes
                if (!resumeSame) {
                    download.chunks.clear()
                }
                download.retries.clear()
                download.requestInFlight = false
                download.endReceived = false
                download.lastFileChunkAt = 0
                download.logged100 = false
                if (!resumeSame) {
                    download.fileChunkRx = 0
                    download.fileChunkStored = 0
                    download.fileChunkDropped = 0
                }
                clearFinalizeTimer()
                clearChunkTimeout()

                setDownloadUiState(true)
                downloadFileName.value = download.filename
                downloadTotalBytes.value = download.totalSize
                downloadReceivedBytes.value = Math.max(download.receivedBytes, downloadReceivedBytes.value || 0)
                downloadBaseOffset.value = download.baseOffset
                downloadCompletionState.value = null
                downloadErrorReason.value = null

                resetDownloadStatsState()
                updateMissingStats()
                resumeMode = false
                persistResumeProgress({ offset: download.receivedBytes, posToken: resumePosToken.value }).catch((e) =>
                    console.warn('[Download] Persist resume failed', e)
                )
            }
            return true
        }

        if (text.startsWith('ft=end')) {
            download.endReceived = true
            const receivedAt = Date.now()
            const sinceLast = download.lastFileChunkAt ? receivedAt - download.lastFileChunkAt : null
            download.lastFileChunkAt = receivedAt
            console.log('[Download] ft=end received', {
                totalSize: download.totalSize,
                receivedBytes: download.receivedBytes,
                chunks: download.chunks.size,
                sinceLastChunkMs: sinceLast,
            })
            persistResumeProgress({ offset: download.receivedBytes, posToken: resumePosToken.value }).catch((e) =>
                console.warn('[Download] Persist resume failed', e)
            )
            scheduleFinalize(FINALIZE_QUIET_WINDOW_MS)
            return true
        }

        if (text.startsWith('ft=progress')) {
            const fields = ensureFields()
            const posField = fields && typeof fields.pos === 'string' ? fields.pos.trim() : ''
            if (posField) resumePosToken.value = posField
            const offsetVal = getNumericField('offset', 'bytes')
            if (Number.isFinite(offsetVal)) {
                download.receivedBytes = Math.max(download.receivedBytes, offsetVal)
                downloadReceivedBytes.value = download.receivedBytes
            }
            persistResumeProgress({ offset: offsetVal, posToken: posField }).catch((e) =>
                console.warn('[Download] Persist resume failed', e)
            )
            return true
        }

        if (text === 'download=1') {
            setDownloadUiState(true)
            return true
        }
        if (text === 'download=0') {
            if (!recoveringChunks.value && !download.requestInFlight) {
                setDownloadUiState(false)
            }
            return true
        }

        if (text.startsWith('sessions=begin')) {
            resetSessionsState()
            const countMatch = text.match(/count=(\d+)/i)
            sessionsExpectedCount.value = countMatch ? parseInt(countMatch[1], 10) || 0 : 0
            scheduleSessionsTimeout()
            return true
        }
        if (text.startsWith('sessions=end')) {
            if (sessionsExpectedCount.value > 0 && sessionsReceivedCount.value < sessionsExpectedCount.value) {
                const retried = await maybeRetrySessions('end')
                if (retried) return true
            }
            markSessionsCompleted().catch((error) => {
                console.warn('[Download] markSessionsCompleted failed', error)
            })
            return true
        }
        if (text.startsWith('session=')) {
            if (!collectingSessions.value) {
                resetSessionsState()
                scheduleSessionsTimeout()
            }
            const match = text.match(/session=([^,]+)/)
            const key = match ? match[1] : text
            if (sessionsSeen.has(key)) return true
            sessionsSeen.add(key)
            sessionsBuffer.value.push(text)
            sessionsReceivedCount.value = sessionsSeen.size
            if (sessionsExpectedCount.value > 0 && sessionsReceivedCount.value >= sessionsExpectedCount.value) {
                markSessionsCompleted().catch((error) => {
                    console.warn('[Download] markSessionsCompleted failed', error)
                })
            }
            return true
        }
        if (collectingSessions.value && text.startsWith('sessions')) {
            return true
        }

        if (text.startsWith('error=')) {
            if (download.active) {
                await handleFtError(text)
                return true
            }
            return false
        }

        return false
    }

    const resetDownload = (status = null, error = null) => {
        download.active = false
        download.filename = ''
        download.totalSize = 0
        download.receivedBytes = 0
        download.storedBytes = 0
        download.startTime = 0
        download.sessionStartBytes = 0
        download.baseOffset = 0
        download.chunks.clear()
        download.formatMode = false
        download.formatModel = null
        download.contentReceiver = null
        download.paused = false
        download.finalized = false
        download.requestInFlight = false
        download.requestOffset = null
        download.requestLen = 0
        download.retries.clear()
        clearChunkTimeout()
        clearFinalizeTimer()
        recoveringChunks.value = false
        resumePosToken.value = null
        persistedContiguous.value = 0
        chunkCount.value = 0
        download.endReceived = false
        download.lastFileChunkAt = 0
        download.logged100 = false
        download.fileChunkRx = 0
        download.fileChunkStored = 0
        download.fileChunkDropped = 0
        resumeMode = false
        resumePersistAt = 0
        resumePersistOffset = 0
        resumePersistName = ''

        setDownloadUiState(false)
        downloadTotalBytes.value = 0
        downloadReceivedBytes.value = 0
        downloadFileName.value = ''
        downloadBaseOffset.value = 0

        if (status) downloadCompletionState.value = status
        if (error) downloadErrorReason.value = error

        resetDownloadStatsState()
        updateMissingStats()
    }

    const pauseDownload = async () => {
        if (!download.active) return false
        try {
            await writeCommand('cancel')
        } catch (error) {
            console.warn('[Download] Cancel command failed', error)
        }

        download.active = false
        download.paused = true
        setDownloadUiState(false)
        downloadCompletionState.value = 'paused'
        downloadErrorReason.value = null
        persistResumeProgress({ offset: download.receivedBytes, posToken: resumePosToken.value }).catch((e) =>
            console.warn('[Download] Persist resume failed', e)
        )
        return true
    }

    const downloadFileFromSD = async (filename, options = {}) => {
        if (download.active) return false
        clearDownloadReady()
        resetDownload()
        download.filename = filename
        const forced = typeof options?.format === 'boolean' ? options.format : null
        const allowFormat = forced !== null ? forced : !options?.forceRaw
        const activeModel =
            allowFormat && (options?.forceModel || (typeof forceModel === 'function' ? forceModel() : forceModel))
        download.formatMode = !!activeModel && allowFormat
        download.formatModel = download.formatMode ? activeModel : null
        download.startTime = Date.now()

        setDownloadUiState(true)
        downloadFileName.value = filename
        const expectedSize = Number.isFinite(options?.expectedSize) ? Number(options.expectedSize) : 0
        downloadTotalBytes.value = expectedSize > 0 ? expectedSize : 0
        downloadReceivedBytes.value = 0
        updateMissingStats()
        downloadBaseOffset.value = 0

        let resumeState = null
        let resumeOffset = 0
        let resumeToken = ''
        if (options?.resume !== false) {
            resumeState = await readResumeState(filename)
            const offset = Number(resumeState?.offset) || 0
            const token = resumeState?.posToken || ''
            if (!offset && !token) {
                const memoryOffset = await readResumeMemoryOffset(filename)
                if (Number.isFinite(memoryOffset) && memoryOffset > 0) {
                    resumeState = {
                        offset: Math.floor(memoryOffset),
                        posToken: '',
                        totalSize: Number(resumeState?.totalSize) || 0,
                    }
                }
            }
        }

        if (resumeState) {
            if (Number.isFinite(resumeState.offset) && resumeState.offset > 0) {
                resumeOffset = Math.floor(resumeState.offset)
            }
            if (resumeState.posToken) {
                resumeToken = resumeState.posToken
            }
            if (resumeState.totalSize && !downloadTotalBytes.value) {
                downloadTotalBytes.value = resumeState.totalSize
            }
            if (resumeState.totalSize && !download.totalSize) {
                download.totalSize = resumeState.totalSize
            }
        }

        if (options?.resume !== false) {
            const cachedChunks = await readChunks(filename)
            if (cachedChunks.length > 0) {
                download.chunks.clear()
                download.storedBytes = 0
                let maxOffset = 0
                for (const chunk of cachedChunks) {
                    download.chunks.set(chunk.offset, chunk.bytes)
                    download.storedBytes += chunk.bytes.length
                    const end = chunk.offset + chunk.bytes.length
                    if (end > maxOffset) maxOffset = end
                }
                chunkCount.value = download.chunks.size
                download.receivedBytes = Math.max(download.receivedBytes, maxOffset)
                downloadReceivedBytes.value = download.receivedBytes
                updateMissingStats()

                const expected = download.totalSize || downloadTotalBytes.value || expectedSize || 0
                const sorted = getSortedChunks()
                const missing = findFirstMissingRange(sorted, expected)
                if (missing) {
                    resumeOffset = missing.start
                } else if (expected > 0) {
                    resumeOffset = expected
                } else {
                    const contiguousEnd = getContiguousEnd(sorted)
                    if (contiguousEnd > 0) {
                        resumeOffset = contiguousEnd
                    }
                }
                resumeMode = true
            }
        }

        download.sessionStartBytes = download.receivedBytes
        downloadBaseOffset.value = resumeOffset > 0 ? resumeOffset : 0
        resumePosToken.value = resumeToken || ''
        if (resumeToken && resumeOffset === resumeState?.offset) {
            await writeCommand(`get=${filename},fp=${resumeToken}`)
        } else if (resumeOffset > 0) {
            await writeCommand(`get=${filename},from=${resumeOffset}`)
        } else {
            await writeCommand(`get=${filename}`)
        }
        return true
    }

    const downloadFileContentFromSD = (filename) =>
        new Promise(async (resolve, reject) => {
            try {
                download.contentReceiver = resolve
                const ok = await downloadFileFromSD(filename, { forceRaw: true })
                if (!ok) reject(new Error('Download failed to start'))
            } catch (error) {
                reject(error)
            }
        })

    const listSessions = async () => {
        if (download.active) return false
        const id = sessionsRequestId.value + 1
        sessionsRequestId.value = id
        sessionsCompletedId.value = 0
        console.trace('[Download] listSessions called', { id })
        await startSessionsRequest({ resetRetry: true })
        return id
    }

    const waitForSessionsCompletion = async (requestId, timeoutMs = 10000) => {
        const start = Date.now()
        return await new Promise((resolve) => {
            const check = () => {
                const done = requestId
                    ? sessionsCompletedId.value === requestId && !collectingSessions.value
                    : !collectingSessions.value
                if (done) return resolve(true)
                if (Date.now() - start >= timeoutMs) return resolve(false)
                setTimeout(check, 120)
            }
            setTimeout(check, 200)
        })
    }

    const clearSessionsTimeout = () => {
        if (sessionsTimeoutId) {
            clearTimeout(sessionsTimeoutId)
            sessionsTimeoutId = null
        }
    }

    const computeResumeInfo = async (files) => {
        if (!files || files.length === 0) return
        await Promise.all(
            files.map(async (file) => {
                try {
                    let saved = await readResumeState(file.name)
                    let offset = Number(saved?.offset) || 0
                    let token = saved?.posToken || ''
                    if (!offset && !token) {
                        const memoryOffset = await readResumeMemoryOffset(file.name)
                        if (Number.isFinite(memoryOffset) && memoryOffset > 0) {
                            saved = { offset: Math.floor(memoryOffset), posToken: '', totalSize: saved?.totalSize || 0 }
                            offset = Number(saved?.offset) || 0
                            token = saved?.posToken || ''
                        }
                    }
                    file.resumeBytes = offset
                    if (file.size > 0) {
                        file.resumePercent = Math.max(0, Math.min(100, Math.round((offset / file.size) * 100)))
                    } else {
                        file.resumePercent = 0
                    }
                } catch {
                    file.resumeBytes = 0
                    file.resumePercent = 0
                }
            })
        )
    }

    const markSessionsCompleted = async () => {
        collectingSessions.value = false
        clearSessionsTimeout()

        const files = []
        const seen = new Set()
        for (const line of sessionsBuffer.value) {
            const match = line.match(/session=([^,]+),size=(\d+)(?:,tag=([^,]+))?/)
            if (match) {
                if (seen.has(match[1])) continue
                seen.add(match[1])
                let timestamp = 0
                const tsMatch = match[1].match(/_(\d{10})\.csv$/)
                if (tsMatch) timestamp = parseInt(tsMatch[1]) * 1000

                files.push({
                    name: match[1],
                    size: parseInt(match[2]) || 0,
                    tag: match[3] || extractLabelFromFilename(match[1]) || '',
                    timestamp,
                    formattedDate: timestamp ? formatDate(timestamp) : '',
                    resumeBytes: 0,
                    resumePercent: 0,
                })
            }
        }

        files.sort((a, b) => b.timestamp - a.timestamp)
        await computeResumeInfo(files)
        sdFiles.value = files
        sessionsCompletedId.value = sessionsRequestId.value
    }

    const listAndGetSDFiles = async ({ timeoutMs = 10000 } = {}) => {
        const reqId = await listSessions()
        if (!reqId) return null
        const ok = await waitForSessionsCompletion(reqId, timeoutMs)
        if (!ok) return null
        return sdFiles.value
    }

    const deleteFileFromSD = async (filename) => {
        if (!filename) return false
        const waitForDelete = startDeleteWait(filename)
        try {
            await writeCommand(`del=${filename}`)
        } catch (error) {
            finishDeleteWait(false)
            throw error
        }
        const ok = await waitForDelete
        if (ok) {
            await computeResumeInfo(sdFiles.value)
            clearResumeState(filename).catch(() => {})
            clearChunks(filename).catch(() => {})
        }
        return ok
    }

    const getResumeInfo = async (filename) => {
        if (!filename) return { canResume: false }
        let resumeState = await readResumeState(filename)
        let offset = Number(resumeState?.offset) || 0
        let posToken = resumeState?.posToken || ''
        if (!offset && !posToken) {
            const memoryOffset = await readResumeMemoryOffset(filename)
            if (Number.isFinite(memoryOffset) && memoryOffset > 0) {
                resumeState = { offset: Math.floor(memoryOffset), posToken: '', totalSize: resumeState?.totalSize || 0 }
                offset = Number(resumeState?.offset) || 0
                posToken = resumeState?.posToken || ''
            }
        }
        const totalSize = Number(resumeState?.totalSize) || 0
        return {
            canResume: offset > 0 || !!posToken,
            offset,
            posToken,
            totalSize,
        }
    }

    const resumePendingDownload = async () => {
        if (download.active) return false
        if (!download.paused || !download.filename) return false
        const expectedSize = download.totalSize || downloadTotalBytes.value || 0
        const options = {
            expectedSize,
            format: download.formatMode,
            forceModel: download.formatModel,
            resume: true,
        }
        download.paused = false
        return downloadFileFromSD(download.filename, options)
    }

    const listDownloadCacheKeys = async () => {
        const kvKeys = await listReinforceDownloadMetaKeys()
        const sqliteResume = await listResumeFiles()
        const sqliteChunks = await listChunkFiles()
        return [
            ...kvKeys,
            ...sqliteResume.map((name) => makeReinforceDownloadResumeKey(name)),
            ...sqliteChunks.map((name) => makeReinforceDownloadChunksKey(name)),
        ]
    }

    const clearDownloadCache = async () => {
        if (download.active) throw new Error('download-active')
        const keys = await listDownloadCacheKeys()
        const memoryNames = await listReinforceDownloadMetaFileNames()
        await clearAllDownloadCache()
        for (const name of memoryNames) {
            await clearReinforceDownloadMetaFor(name)
        }
        await computeResumeInfo(sdFiles.value)
        return { cleared: keys.length }
    }

    const clearDownloadCacheFor = async (names) => {
        if (download.active) throw new Error('download-active')
        let cleared = 0
        for (const name of names) {
            try {
                await clearReinforceDownloadMetaFor(name)
                await clearResumeState(name)
                await clearChunks(name)
                cleared += 3
            } catch {}
        }
        await computeResumeInfo(sdFiles.value)
        return { cleared }
    }

    const scanStaleDownloadCache = async () => {
        const cacheNames = new Set()
        const resumeNames = await listResumeFiles()
        const chunkNames = await listChunkFiles()
        for (const name of resumeNames) cacheNames.add(name)
        for (const name of chunkNames) cacheNames.add(name)
        const memoryNames = await listReinforceDownloadMetaFileNames()
        for (const name of memoryNames) cacheNames.add(name)
        const present = new Set(sdFiles.value.map((file) => file.name))
        const stale = Array.from(cacheNames).filter((name) => !present.has(name))
        staleCache.value = stale
        return stale
    }

    const fetchSizeForFile = async (filename) => {
        const file = sdFiles.value.find((item) => item.name === filename)
        return file?.size || null
    }

    const resetDownloadControllerState = async () => {
        resetDownload()
        return true
    }

    const getCachedFileText = async () => ({ ok: false })

    return {
        downloadStats,
        downloadMissing,
        collectingSessions,
        sessionsBuffer,
        sessionsExpectedCount,
        sessionsReceivedCount,
        sessionsRequestId,
        sessionsCompletedId,
        sessionsRetryCount,
        resetDownloadStatsState,
        clearEtaState,
        recoveringChunks,
        resumeReload,
        handleFtLine,
        handleFileChunkDv,
        handleFtError,
        handleDeleteStatus,
        downloadFileFromSD,
        downloadFileContentFromSD,
        listSessions,
        deleteFileFromSD,
        listAndGetSDFiles,
        getResumeInfo,
        listDownloadCacheKeys,
        clearDownloadCache,
        clearDownloadCacheFor,
        scanStaleDownloadCache,
        fetchSizeForFile,
        pauseDownload,
        resetDownloadControllerState,
        markSessionsCompleted,
        clearSessionsTimeout,
        resumePendingDownload,
        getCachedFileText,
        resumePosToken,
        chunkCount,
        persistedContiguous,
        crcErrorCount,
        downloadReadyUrl,
        downloadReadyName,
        downloadReadyAt,
        clearDownloadReady,
    }
}
